import React from 'react';
import { NavLink, Group, Text } from '@mantine/core';
import { useLocation } from 'react-router-dom';
import { useNavigateWithParams } from '@/hooks/useNavigateWithParams';
import { useAuth0 } from '@/hooks/useAuth0';
import type { NavSection } from '@/utils/routes';
import { ProgressIndicator } from './ProgressIndicator';

interface NavbarSectionProps {
  section: NavSection;
}

export const NavbarSection = ({ section }: NavbarSectionProps) => {
  const location = useLocation();
  const navigateWithParams = useNavigateWithParams();
  const { isAuthenticated } = useAuth0();

  if (!isAuthenticated) {
    return null;
  }

  const containsActivePage = React.useMemo(() => {
    return section.pages.some((p) => location.pathname === p.path);
  }, [location.pathname, section.pages]);

  const [opened, setOpened] = React.useState<boolean>(containsActivePage);

  React.useEffect(() => {
    // Always sync opened state to whether this section contains the active page
    setOpened(containsActivePage);
  }, [containsActivePage]);

  return (
    <NavLink
      label={
        <Group gap="xs" align="center">
          {section.status && (
            <ProgressIndicator status={section.status} size={10} />
          )}
          <Text size="sm" fw={500}>
            {section.label}
          </Text>
        </Group>
      }
      opened={opened}
      childrenOffset={28}
      disabled={section.enabled === false}
      style={{ opacity: section.enabled === false ? 0.6 : 1 }}
      onClick={() => setOpened((o) => !o)}
    >
      {section.pages.map((page) => {
        const isPageActive = location.pathname === page.path;

        return (
          <NavLink
            key={page.id}
            label={
              <Group gap="xs" align="center" style={{ minHeight: '24px' }}>
                {page.status && (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <ProgressIndicator status={page.status} size={8} />
                  </div>
                )}
                <Text size="sm" c={page.enabled === false ? 'dimmed' : undefined}>
                  {page.label}
                </Text>
              </Group>
            }
            onClick={() => page.enabled !== false && navigateWithParams(page.path)}
            active={isPageActive}
            disabled={page.enabled === false}
            style={{ 
              cursor: page.enabled === false ? 'not-allowed' : 'pointer',
              opacity: page.enabled === false ? 0.6 : 1
            }}
          />
        );
      })}
    </NavLink>
  );
};